import React, { useState, useMemo, useEffect, useRef } from 'react';
import { 
  Play, Pause, SkipForward, SkipBack, Volume2, 
  X, ChevronUp, ChevronDown, BookOpen, Headphones, 
  Sparkles, Search, Layers, Clock, ShieldCheck, 
  RotateCcw, Sliders, CheckCircle2, MessageSquareText,
  Flame, Activity, ShieldAlert, GraduationCap, Zap, Minimize2, Maximize2
} from 'lucide-react';
import { useAudio } from './AudioContext';
import { useThemeTokens } from '../hooks/useActiveTheme';
import { SponsoredService } from '../services/sponsoredService';
import { ConceptAudioTrack } from '../types';
import { DEFAULT_NURSING_CONCEPT_TRACKS } from '../data/audioTracksData';

export type { ConceptAudioTrack };
export const NURSING_CONCEPT_TRACKS = DEFAULT_NURSING_CONCEPT_TRACKS;

interface MiniPlayerProps {
  variant?: 'floating' | 'inline';
  className?: string;
  defaultConceptId?: string;
}

export const MiniPlayer: React.FC<MiniPlayerProps> = ({ 
  variant = 'floating', 
  className = '',
  defaultConceptId 
}) => {
  const { 
    currentTrack, isPlaying, progress, duration, playbackRate,
    playTrack, togglePlay, seek, setPlaybackRate, jump 
  } = useAudio();
  const tokens = useThemeTokens();

  // Load live feature flags & admin tracks configuration
  const [adConfig, setAdConfig] = useState(() => SponsoredService.getConfig());

  useEffect(() => {
    const handleConfigUpdate = () => {
      setAdConfig(SponsoredService.getConfig());
    };
    window.addEventListener('sponsored-config-updated', handleConfigUpdate);
    return () => window.removeEventListener('sponsored-config-updated', handleConfigUpdate);
  }, []);

  const activeTracks: ConceptAudioTrack[] = useMemo(() => {
    if (adConfig.miniPlayerTracks && Array.isArray(adConfig.miniPlayerTracks) && adConfig.miniPlayerTracks.length > 0) {
      return adConfig.miniPlayerTracks;
    }
    return DEFAULT_NURSING_CONCEPT_TRACKS;
  }, [adConfig.miniPlayerTracks]);

  // Local state for widget expand/collapse & search
  const [isExpanded, setIsExpanded] = useState(false);
  const [activeCategory, setActiveCategory] = useState<string>('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [showScriptDrawer, setShowScriptDrawer] = useState(false);
  const [useSpeechSynth, setUseSpeechSynth] = useState(false);

  // Determine active track in MiniPlayer scope
  const activeTrackObj = useMemo(() => {
    if (currentTrack) {
      const match = activeTracks.find(t => t.source === currentTrack.source || t.title === currentTrack.title);
      if (match) return match;
      return {
        id: 'custom',
        title: currentTrack.title,
        conceptCategory: (currentTrack.conceptCategory as any) || 'Clinical & Vitals',
        duration: currentTrack.duration || '3:00',
        img: currentTrack.img,
        source: currentTrack.source,
        script: currentTrack.script || 'Audio summary playing for nursing concept.',
        highYieldPoints: ['Active registry audio session synchronized']
      } as ConceptAudioTrack;
    }
    return activeTracks[0] || DEFAULT_NURSING_CONCEPT_TRACKS[0];
  }, [currentTrack, activeTracks]);

  // Filtered concept list
  const filteredTracks = useMemo(() => {
    return activeTracks.filter(track => {
      const matchesCat = activeCategory === 'All' || track.conceptCategory === activeCategory;
      const matchesSearch = !searchQuery || 
        track.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        track.script.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesCat && matchesSearch;
    });
  }, [activeTracks, activeCategory, searchQuery]);

  const activeConceptUtteranceRef = useRef<SpeechSynthesisUtterance | null>(null);

  useEffect(() => {
    return () => {
      if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
        window.speechSynthesis.cancel();
        activeConceptUtteranceRef.current = null;
      }
    };
  }, []);

  // Check Feature Flag: If super admin disabled Mini Player in System Control, hide component completely
  if (adConfig.isMiniPlayerEnabled === false) {
    return null;
  }

  // Speech synthesis fallback mechanism
  const handlePlayConcept = (track: ConceptAudioTrack) => {
    if (useSpeechSynth && typeof window !== 'undefined' && 'speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      const cleanScript = (track.script || '')
        .replace(/<[^>]*>/g, ' ')
        .replace(/[*#`_~]/g, '')
        .replace(/\s+/g, ' ')
        .trim();
      const utterance = new SpeechSynthesisUtterance(cleanScript);
      activeConceptUtteranceRef.current = utterance;
      utterance.rate = playbackRate;
      utterance.onend = () => {
        activeConceptUtteranceRef.current = null;
      };
      utterance.onerror = () => {
        activeConceptUtteranceRef.current = null;
      };
      window.speechSynthesis.speak(utterance);
    }

    playTrack({
      title: track.title,
      img: track.img,
      source: track.source,
      duration: track.duration,
      conceptCategory: track.conceptCategory,
      script: track.script
    });
  };

  const categories = ['All', 'Clinical & Vitals', 'Safety & Prevention', 'ADLs & Mobility', 'Personal ADL Care', 'Principles & Rights', 'Board Prep & Basics'];

  const formatTime = (time: number) => {
    if (isNaN(time)) return '0:00';
    const m = Math.floor(time / 60);
    const s = Math.floor(time % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  // -------------------------------------------------------------
  // INLINE VARIANT: Renders as an embedded card on Dashboard / Guides
  // -------------------------------------------------------------
  if (variant === 'inline') {
    return (
      <div className={`bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 text-white rounded-[2.5rem] p-6 md:p-8 border border-indigo-500/30 shadow-xl relative overflow-hidden ${className}`}>
        {/* Background Decorative Glow */}
        <div className="absolute top-0 right-0 w-80 h-80 bg-indigo-500/10 blur-3xl pointer-events-none rounded-full" />
        
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6 relative z-10">
          {/* Header Info */}
          <div className="space-y-2 max-w-xl">
            <div className="flex items-center gap-2">
              <span className="px-3 py-1 bg-indigo-500/20 text-indigo-300 border border-indigo-500/30 rounded-full text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5">
                <Headphones size={12} className="text-indigo-400 animate-pulse" />
                Nursing Concept Audio Summaries
              </span>
              <span className="px-2.5 py-1 bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 rounded-full text-[9px] font-extrabold uppercase">
                10 High-Yield Concepts
              </span>
            </div>
            <h3 className="text-xl md:text-2xl font-black text-white tracking-tight">
              Audio Summary Mini Player
            </h3>
            <p className="text-xs text-slate-300 font-medium leading-relaxed">
              Listen to concise 3-minute voice summaries of key nursing procedures, vital signs, and safety protocols while reviewing your study materials or navigating the app.
            </p>
          </div>

          {/* Quick Play Main Track Button */}
          <div className="flex items-center gap-3 shrink-0">
            <button
              onClick={() => handlePlayConcept(activeTrackObj)}
              className={`px-6 py-3.5 ${tokens.primaryBtn || 'bg-indigo-600 hover:bg-indigo-500'} text-white font-black text-xs md:text-sm rounded-2xl shadow-xl flex items-center gap-2.5 transition-all active:scale-95 group`}
            >
              {isPlaying && currentTrack?.source === activeTrackObj.source ? (
                <>
                  <Pause size={18} />
                  <span>Pause Concept Audio</span>
                </>
              ) : (
                <>
                  <Play size={18} fill="currentColor" />
                  <span>Listen: {activeTrackObj.title.split(':')[0]}</span>
                </>
              )}
            </button>
            <button
              onClick={() => setShowScriptDrawer(!showScriptDrawer)}
              className="p-3 bg-white/10 hover:bg-white/20 text-white rounded-2xl transition-all border border-white/10"
              title="View Audio Script Summary"
            >
              <MessageSquareText size={18} />
            </button>
          </div>
        </div>

        {/* Selected Concept Player Bar */}
        <div className="mt-6 p-4 bg-slate-950/70 rounded-2xl border border-white/10 flex flex-col md:flex-row items-center gap-4 relative z-10">
          <img 
            src={activeTrackObj.img} 
            alt={activeTrackObj.title} 
            className="w-14 h-14 rounded-xl object-cover border border-white/10 shrink-0" 
          />
          <div className="flex-1 min-w-0 w-full space-y-1">
            <div className="flex items-center justify-between gap-2">
              <span className="text-[10px] font-black uppercase text-indigo-400 tracking-wider">
                {activeTrackObj.conceptCategory}
              </span>
              <span className="text-[10px] font-extrabold text-slate-400 tabular-nums">
                {formatTime(progress)} / {activeTrackObj.duration}
              </span>
            </div>
            <p className="text-xs font-bold text-white truncate">
              {activeTrackObj.title}
            </p>
            {/* Progress Scrub */}
            <div 
              className="h-1.5 bg-white/10 rounded-full overflow-hidden cursor-pointer relative group"
              onClick={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                const pos = (e.clientX - rect.left) / rect.width;
                seek(pos * (duration || 210));
              }}
            >
              <div 
                className="h-full bg-indigo-500 rounded-full transition-all duration-300"
                style={{ width: `${(progress / (duration || 210)) * 100}%` }}
              />
            </div>
          </div>

          {/* Speed selector */}
          <div className="flex items-center gap-2 shrink-0">
            {[1, 1.25, 1.5].map(rate => (
              <button
                key={rate}
                onClick={() => setPlaybackRate(rate)}
                className={`px-2 py-1 rounded-lg text-[9px] font-black uppercase transition-all ${
                  playbackRate === rate 
                    ? 'bg-indigo-600 text-white' 
                    : 'bg-white/5 text-slate-400 hover:text-white'
                }`}
              >
                {rate}x
              </button>
            ))}
          </div>
        </div>

        {/* Concept Script Reader Drawer */}
        {showScriptDrawer && (
          <div className="mt-4 p-5 bg-slate-950/90 rounded-2xl border border-indigo-500/30 space-y-3 animate-fade-in relative z-10">
            <div className="flex items-center justify-between border-b border-white/10 pb-2">
              <span className="text-xs font-black text-indigo-300 uppercase tracking-wider flex items-center gap-1.5">
                <BookOpen size={14} /> High-Yield Concept Script Summary
              </span>
              <button onClick={() => setShowScriptDrawer(false)} className="text-slate-400 hover:text-white">
                <X size={14} />
              </button>
            </div>
            <p className="text-xs text-slate-200 leading-relaxed font-medium">
              "{activeTrackObj.script}"
            </p>
            <div className="pt-2 border-t border-white/10 flex flex-wrap gap-2">
              {activeTrackObj.highYieldPoints.map((pt, i) => (
                <span key={i} className="px-2.5 py-1 bg-indigo-500/20 text-indigo-300 border border-indigo-500/30 rounded-lg text-[10px] font-bold flex items-center gap-1">
                  <CheckCircle2 size={11} className="text-indigo-400" />
                  {pt}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Catalog Carousel / Grid */}
        <div className="mt-6 pt-6 border-t border-white/10 space-y-3 relative z-10">
          <div className="flex items-center justify-between">
            <span className="text-xs font-black uppercase text-slate-300 tracking-wider">
              Browse Concept Library ({filteredTracks.length})
            </span>
            {/* Category Filter Pills */}
            <div className="flex items-center gap-1.5 overflow-x-auto scrollbar-none max-w-full pb-1">
              {categories.slice(0, 4).map(cat => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`px-2.5 py-1 rounded-xl text-[9px] font-black uppercase whitespace-nowrap transition-all ${
                    activeCategory === cat 
                      ? 'bg-white text-slate-900 shadow-xs' 
                      : 'bg-white/5 text-slate-400 hover:text-white'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {filteredTracks.slice(0, 6).map(track => {
              const isSelected = activeTrackObj.id === track.id;
              const isThisPlaying = isSelected && isPlaying;
              return (
                <div
                  key={track.id}
                  onClick={() => handlePlayConcept(track)}
                  className={`p-3 rounded-2xl border transition-all cursor-pointer flex items-center gap-3 ${
                    isSelected 
                      ? 'bg-indigo-600/30 border-indigo-500 text-white shadow-lg' 
                      : 'bg-slate-950/40 border-white/5 hover:border-white/20 text-slate-300'
                  }`}
                >
                  <img src={track.img} alt="" className="w-10 h-10 rounded-lg object-cover shrink-0" />
                  <div className="min-w-0 flex-1 space-y-0.5">
                    <p className="text-[9px] font-black uppercase text-indigo-400 tracking-wider">{track.conceptCategory}</p>
                    <p className="text-xs font-bold text-white truncate">{track.title}</p>
                    <p className="text-[9px] font-medium text-slate-400">{track.duration}</p>
                  </div>
                  <div className={`p-2 rounded-xl shrink-0 ${isThisPlaying ? 'bg-indigo-600 text-white animate-pulse' : 'bg-white/10 text-slate-300'}`}>
                    {isThisPlaying ? <Pause size={14} /> : <Play size={14} fill="currentColor" />}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // -------------------------------------------------------------
  // FLOATING VARIANT: Persistent Bottom-Right Dock Widget
  // -------------------------------------------------------------
  return (
    <div className={`fixed bottom-4 right-4 z-[160] transition-all duration-300 max-w-[calc(100vw-2rem)] ${className}`}>
      {!isExpanded ? (
        /* MINIMIZED PILL BADGE DOCK */
        <div className="flex items-center gap-2 p-1.5 pr-4 bg-slate-900/95 dark:bg-slate-900/98 text-white rounded-full border border-indigo-500/40 shadow-2xl backdrop-blur-xl hover:border-indigo-400 transition-all cursor-pointer group">
          <button
            onClick={() => handlePlayConcept(activeTrackObj)}
            className="w-9 h-9 bg-indigo-600 hover:bg-indigo-500 text-white rounded-full flex items-center justify-center shadow-lg transition-transform active:scale-95 shrink-0"
            title={isPlaying ? "Pause Audio Summary" : "Play Concept Audio Summary"}
          >
            {isPlaying ? <Pause size={16} /> : <Play size={16} fill="currentColor" className="ml-0.5" />}
          </button>

          <div 
            onClick={() => setIsExpanded(true)}
            className="flex items-center gap-2 min-w-0 max-w-[200px] sm:max-w-[260px]"
          >
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                <p className="text-[8px] font-black uppercase text-indigo-300 tracking-widest truncate">
                  Mini Audio Player • {activeTrackObj.conceptCategory}
                </p>
              </div>
              <p className="text-xs font-bold text-white truncate">
                {activeTrackObj.title}
              </p>
            </div>
            <Maximize2 size={14} className="text-slate-400 group-hover:text-white shrink-0 ml-1" />
          </div>
        </div>
      ) : (
        /* EXPANDED MINI PLAYER DOCK PANEL */
        <div className="w-[calc(100vw-2rem)] sm:w-[380px] max-w-[380px] bg-slate-950/98 text-white rounded-[2rem] border border-indigo-500/40 shadow-2xl p-4 sm:p-5 backdrop-blur-2xl space-y-4 animate-scale-up relative overflow-hidden">
          {/* Top Header */}
          <div className="flex items-center justify-between border-b border-white/10 pb-3">
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-indigo-500/20 text-indigo-400 rounded-lg">
                <Headphones size={16} />
              </div>
              <div>
                <h4 className="text-xs font-black text-white uppercase tracking-wider">
                  Nursing Concept Audio Hub
                </h4>
                <p className="text-[9px] text-slate-400 font-medium">
                  Audio summaries while you study
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-1">
              <button
                onClick={() => setIsExpanded(false)}
                className="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-white/10 transition-colors"
                title="Minimize Mini Player"
              >
                <Minimize2 size={16} />
              </button>
            </div>
          </div>

          {/* Active Track Banner */}
          <div className="bg-slate-900/90 rounded-2xl p-3.5 border border-white/10 space-y-3">
            <div className="flex items-center gap-3">
              <img 
                src={activeTrackObj.img} 
                alt={activeTrackObj.title} 
                className="w-12 h-12 rounded-xl object-cover border border-white/10 shrink-0 shadow-md" 
              />
              <div className="min-w-0 flex-1">
                <span className="px-2 py-0.5 bg-indigo-500/20 text-indigo-300 border border-indigo-500/30 rounded-md text-[8px] font-black uppercase tracking-wider">
                  {activeTrackObj.conceptCategory}
                </span>
                <p className="text-xs font-black text-white truncate mt-1">
                  {activeTrackObj.title}
                </p>
                <p className="text-[9px] font-bold text-slate-400 tabular-nums">
                  {formatTime(progress)} / {activeTrackObj.duration}
                </p>
              </div>
            </div>

            {/* Scrub Bar */}
            <div 
              className="h-1.5 bg-white/10 rounded-full overflow-hidden cursor-pointer relative"
              onClick={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                const pos = (e.clientX - rect.left) / rect.width;
                seek(pos * (duration || 210));
              }}
            >
              <div 
                className="h-full bg-indigo-500 rounded-full transition-all duration-300"
                style={{ width: `${(progress / (duration || 210)) * 100}%` }}
              />
            </div>

            {/* Main Controls */}
            <div className="flex items-center justify-between pt-1">
              <button 
                onClick={() => jump(-15)} 
                className="p-1.5 text-slate-400 hover:text-white transition-colors" 
                title="Rewind 15s"
              >
                <SkipBack size={16} />
              </button>

              <button
                onClick={() => handlePlayConcept(activeTrackObj)}
                className="w-10 h-10 bg-indigo-600 hover:bg-indigo-500 text-white rounded-full flex items-center justify-center shadow-lg transition-transform active:scale-95"
              >
                {isPlaying ? <Pause size={18} /> : <Play size={18} fill="currentColor" className="ml-0.5" />}
              </button>

              <button 
                onClick={() => jump(15)} 
                className="p-1.5 text-slate-400 hover:text-white transition-colors" 
                title="Forward 15s"
              >
                <SkipForward size={16} />
              </button>

              {/* Speed toggle */}
              <button
                onClick={() => {
                  const nextRate = playbackRate === 1 ? 1.25 : playbackRate === 1.25 ? 1.5 : 1;
                  setPlaybackRate(nextRate);
                }}
                className="px-2 py-1 bg-white/10 hover:bg-white/20 text-slate-200 rounded-lg text-[9px] font-black uppercase transition-colors"
                title="Playback Speed"
              >
                {playbackRate}x
              </button>

              {/* Script Drawer Toggle */}
              <button
                onClick={() => setShowScriptDrawer(!showScriptDrawer)}
                className={`p-1.5 rounded-lg transition-colors ${showScriptDrawer ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:text-white'}`}
                title="View Concept Script"
              >
                <MessageSquareText size={16} />
              </button>
            </div>
          </div>

          {/* Script Drawer in Floating Mode */}
          {showScriptDrawer && (
            <div 
              role="region" 
              aria-label="Concept script reader summary"
              className="p-3.5 bg-slate-900 rounded-xl border border-indigo-500/30 text-xs space-y-2 max-h-40 overflow-y-auto scrollbar-thin"
            >
              <p className="font-black text-indigo-300 text-[10px] uppercase tracking-wider">
                Concept Audio Script:
              </p>
              <p className="text-[11px] text-slate-200 leading-relaxed font-medium break-words">
                "{activeTrackObj.script}"
              </p>
            </div>
          )}

          {/* Concept Search & Category Filter */}
          <div className="space-y-2">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Search concepts (e.g., vitals, PPE)..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-1.5 bg-slate-900 border border-white/10 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-none focus:border-indigo-500"
              />
            </div>

            {/* Compact Category Filter */}
            <div className="flex items-center gap-1 overflow-x-auto scrollbar-none pb-1">
              {categories.map(cat => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`px-2 py-1 rounded-lg text-[8px] font-black uppercase whitespace-nowrap transition-all ${
                    activeCategory === cat 
                      ? 'bg-indigo-600 text-white' 
                      : 'bg-white/5 text-slate-400 hover:text-white'
                  }`}
                >
                  {cat === 'All' ? 'All' : cat.split(' ')[0]}
                </button>
              ))}
            </div>
          </div>

          {/* Tracks List */}
          <div className="max-h-44 overflow-y-auto space-y-1.5 pr-1 scrollbar-thin">
            {filteredTracks.map(track => {
              const isSelected = activeTrackObj.id === track.id;
              const isThisPlaying = isSelected && isPlaying;
              return (
                <div
                  key={track.id}
                  onClick={() => handlePlayConcept(track)}
                  className={`p-2 rounded-xl border transition-all cursor-pointer flex items-center justify-between gap-2 ${
                    isSelected 
                      ? 'bg-indigo-600/30 border-indigo-500 text-white' 
                      : 'bg-slate-900/50 border-white/5 hover:border-white/20 text-slate-300'
                  }`}
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-[8px] font-black text-indigo-400 uppercase tracking-wider">{track.conceptCategory}</p>
                    <p className="text-xs font-bold truncate">{track.title}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-[9px] text-slate-400 font-bold tabular-nums">{track.duration}</span>
                    <div className={`p-1.5 rounded-lg ${isThisPlaying ? 'bg-indigo-600 text-white animate-pulse' : 'bg-white/10 text-slate-300'}`}>
                      {isThisPlaying ? <Pause size={12} /> : <Play size={12} fill="currentColor" />}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
